import { useMemo } from 'react';

type Options = {
    edgeThreshold?: number; // px from edge to start scrolling
    maxStep?: number; // px per onDrag call at the very edge
    horizontal?: boolean; // also scroll horizontally
    debug?: boolean; // logs
};

/** Find the nearest scrollable ancestor; falls back to window if needed */
function getScrollParent(node: HTMLElement | null): HTMLElement | 'window' {
    let el: HTMLElement | null = node;
    while (el && el !== document.body) {
        const style = getComputedStyle(el);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        const canScrollY = (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
        const canScrollX = (overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth;
        if (canScrollY || canScrollX) return el;
        el = el.parentElement;
    }
    return 'window';
}

export function useEdgeAutoScroll(
    gridContainerRef: React.RefObject<HTMLElement>,
    { edgeThreshold = 80, maxStep = 24, horizontal = false, debug = false }: Options = {},
) {
    const api = useMemo(() => {
        let scrollTarget: HTMLElement | 'window' | null = null;

        const ensureTarget = () => {
            if (scrollTarget) return scrollTarget;
            scrollTarget = getScrollParent(gridContainerRef.current || null);
            return scrollTarget;
        };

        const log = (...args: any[]) => debug && console.log('[edge-scroll]', ...args);

        /** Call this from RGL's onDrag */
        const onDrag = (e: MouseEvent | TouchEvent | PointerEvent) => {
            const target = ensureTarget();
            const root = gridContainerRef.current;
            if (!root) return;

            // Pointer coords
            let clientX = 0,
                clientY = 0;
            if ('clientX' in e) {
                clientX = (e as MouseEvent).clientX;
                clientY = (e as MouseEvent).clientY;
            } else if ('touches' in e && (e as TouchEvent).touches[0]) {
                clientX = (e as TouchEvent).touches[0].clientX;
                clientY = (e as TouchEvent).touches[0].clientY;
            }

            // Edge distances relative to the *visible box* of the scroll target
            const rect =
                target === 'window'
                    ? { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight }
                    : (target as HTMLElement).getBoundingClientRect();

            const dyTop = clientY - rect.top;
            const dyBot = rect.bottom - clientY;

            let vy = 0;
            if (dyTop < edgeThreshold) vy = -ramp(edgeThreshold - dyTop, edgeThreshold, maxStep);
            else if (dyBot < edgeThreshold) vy = +ramp(edgeThreshold - dyBot, edgeThreshold, maxStep);

            let vx = 0;
            if (horizontal) {
                const dxLeft = clientX - rect.left;
                const dxRight = rect.right - clientX;
                if (dxLeft < edgeThreshold) vx = -ramp(edgeThreshold - dxLeft, edgeThreshold, maxStep);
                else if (dxRight < edgeThreshold) vx = +ramp(edgeThreshold - dxRight, edgeThreshold, maxStep);
            }

            if (!vx && !vy) return;

            // Perform the scroll
            if (target === 'window') {
                window.scrollBy(vx, vy);
            } else {
                (target as HTMLElement).scrollBy({ top: vy, left: vx, behavior: 'auto' });
            }
            log({ vx, vy });
        };

        const ramp = (dist: number, threshold: number, max: number) => {
            const t = Math.min(1, Math.max(0, dist / threshold));
            return max * (t * t); // quadratic ramp feels nice
        };

        // Optional: if your grid unmounts while dragging, no global listeners to clean up.
        return { onDrag };
    }, [gridContainerRef, edgeThreshold, maxStep, horizontal, debug]);

    return api; // { onDrag }
}
